/**
 * @license
 * Copyright 厦门乾元盛世科技有限公司 All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file.
 */


import { Component, Input, ElementRef, Renderer, ViewEncapsulation } from '@angular/core';
import { HostBinding, OnChanges, SimpleChanges, SimpleChange } from '@angular/core';
import { isPresent } from '../util/lang';
import { onNextFrame } from '../util/anim.frame';
import classNames from 'classnames';

export interface Digit {
    n: number;
    position: number;
};
/**
 * 徽标数
 */
export class ScrollNumber implements OnChanges {
/**
 * 样式前缀
 */
prefixCls = 'ant-scroll-number';
/**
 * 展示的数字，大于 overflowCount 时显示为 `${overflowCount}+`，为 0 时隐藏。 Number to show in badge
 */
count: number;
/**
 * 与count同级，如果非空时则只显示text，不显示count
 */
text: string;
/**
 * 用户CSS样式
 */
class: string;
private lastCount: number;
/**
 * 当前count拆分后的数字
 */
public digits: Digit[] = [];
/**
 * 上次count拆分后的数字
 */
public lastDigits: Digit[] = [];
private defaultNumItems: number[];
private resetPosition = true;
/**
 * @param {?} _renderer
 * @param {?} _elementRef
 */
constructor(private _renderer: Renderer,
private _elementRef: ElementRef) {

    }
/**
 * @return {?}
 */
get classes(): string {
        return classNames(this.prefixCls, this.class);
    }
/**
 * @param {?} changes
 * @return {?}
 */
ngOnChanges(changes: SimpleChanges): void {
        const /** @type {?} */ countChange: SimpleChange = changes['count'];
        if (countChange) { // count 发生变化，如从5增加到6，则previousValue=5，count=currentValue=6
            this.lastCount = countChange.previousValue;
            this.lastDigits = this.digits;
            this.resetPosition = true;

            this.lastDigits = this.getDigits(this.lastCount, true);
            this.digits = this.getDigits(this.count, false);
            this.setPosition(this.lastDigits, this.digits);

            onNextFrame(() => {
                this.resetPosition = false;
                const /** @type {?} */ newDigits = this.getDigits(this.count, true);
                this.setPosition(newDigits, this.digits);
            });
        }
    }
/**
 * @param {?} fromDigits
 * @param {?} toDigits
 * @return {?}
 */
setPosition(fromDigits: Digit[], toDigits: Digit[]): void {
        const /** @type {?} */ len1 = fromDigits.length;
        const /** @type {?} */ len2 = toDigits.length;
        for (let /** @type {?} */ i = 0; i < len2; i++) {
            const /** @type {?} */ digit = fromDigits[len1 - len2 + i];
            if (digit) {
                toDigits[i].position = digit.position;
            }
        }
    }
/**
 * 获取拆分后的数字在`30`个NumItems中的位置，用于生成translateY(x%)的值
 * 
 * @param {?} num
 * @param {?} i num在拆分后的数字数组中的索引
 * @return {?}
 */
getPosition(num: number, i: number) {
        if (this.resetPosition) { // 用于复位数据，位置只会在十几的范围。等到动画执行时，将从十几移动至几或者二十几。
            return 10 + num;
        }

        // 与前一个值进行比较，才能确定是上滑，还是下滑
        // 从右侧取数，如数字123拆分后为[1,2,3]，则当i=0时，返回3(实际的个位数数字)
        const /** @type {?} */ currDigit = num;
        const /** @type {?} */ last = this.lastDigits[this.lastDigits.length - this.digits.length + i];
        const /** @type {?} */ lastDigit = (last && last.n) || 0;


        // 同方向，则在同一侧切换数字。若count>lastCount则数字应从下往上滑动
        if (this.count > this.lastCount) {
            if (currDigit >= lastDigit) { // 例如：数值从18->19的「个位数」，position从18->19
                return 10 + num;
            }
            return 20 + num; // 例如：数值从19->20的「个位数」，position从19->20
        } else { // 若count<lastCount则数字应从上往下滑动
            if (currDigit <= lastDigit) { // 例如：数值从19->18的「个位数」，position从19->18
                return 10 + num;
            }
            return num; // 例如：数值从20->19的「个位数」，position从10->9
        }
    }
/**
 * 将数值拆分为数字组合，例如: 123 => [1,2,3]，倒序是为了从“个位数”开始匹配
 * 
 * @param {?} value 原始数字
 * @param {?} getPos
 * @return {?} 数列
 */
getDigits(value: number, getPos: boolean): Digit[] {
        if (!isPresent(value) || isNaN(value)) {
            return [];
        }

        return value.toString().split('').map((str: string, i: number) => {
            const /** @type {?} */ n: number = Number(str);
            const /** @type {?} */ position = getPos ? this.getPosition(n, i) : 0;
            return { n, position };
        });
    }
/**
 * 生成数字序列，从0开始，例如：getNumItems(5) => [0,1,2,3,4]
 * 
 * @return {?} 数列
 */
getNumItems(): number[] {
        if (!this.defaultNumItems) {
            const /** @type {?} */ size = 30;
            this.defaultNumItems = Array.from(new Array<any>(size), (val: any, index: number) => index);
        }
        return this.defaultNumItems;
    }
/**
 * 获取拆分后的数字所在span的样式
 * 
 * @param {?} i num在拆分后的数字数组中的索引
 * @return {?}
 */
getNumElementStyle(i: number): any {
        const /** @type {?} */ position = this.digits[i].position;
        const /** @type {?} */ noTransition = this.lastDigits.length === 0 || this.lastDigits[this.lastDigits.length - i - 1] === undefined;

        return {
            'transition': noTransition ? 'none' : null,
            '-ms-transform': `translateY(${-position * 100}%)`,
            '-webkit-transform': `translateY(${-position * 100}%)`,
            'transform': `translateY(${-position * 100}%)`
        };
    }

static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
    selector: 'scroll-number',
    templateUrl: './scroll.number.html',
    encapsulation: ViewEncapsulation.None
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Renderer, },
{type: ElementRef, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'prefixCls': [{ type: Input },],
'count': [{ type: Input },],
'text': [{ type: Input },],
'class': [{ type: Input },],
'classes': [{ type: HostBinding, args: ['class', ] },],
};
}

function ScrollNumber_tsickle_Closure_declarations() {
/** @type {?} */
ScrollNumber.decorators;
/**
 * @nocollapse
 * @type {?}
 */
ScrollNumber.ctorParameters;
/** @type {?} */
ScrollNumber.propDecorators;
/**
 * 样式前缀
 * @type {?}
 */
ScrollNumber.prototype.prefixCls;
/**
 * 展示的数字，大于 overflowCount 时显示为 `${overflowCount}+`，为 0 时隐藏。 Number to show in badge
 * @type {?}
 */
ScrollNumber.prototype.count;
/**
 * 与count同级，如果非空时则只显示text，不显示count
 * @type {?}
 */
ScrollNumber.prototype.text;
/**
 * 用户CSS样式
 * @type {?}
 */
ScrollNumber.prototype.class;
/** @type {?} */
ScrollNumber.prototype.lastCount;
/**
 * 当前count拆分后的数字
 * @type {?}
 */
ScrollNumber.prototype.digits;
/**
 * 上次count拆分后的数字
 * @type {?}
 */
ScrollNumber.prototype.lastDigits;
/** @type {?} */
ScrollNumber.prototype.defaultNumItems;
/** @type {?} */
ScrollNumber.prototype.resetPosition;
/** @type {?} */
ScrollNumber.prototype._renderer;
/** @type {?} */
ScrollNumber.prototype._elementRef;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
